import { formatDate } from '../../../lib/formatDate';
import AppTile from './AppTile';
import { appKind, appLabel, appName, isUnnamed, withSuffix } from './groupApps';

// Broken-link glyph for Disconnect (revoking the app's Google access). Inline
// SVG like the rest of the app's icons; decorative, the button text names it.
function UnlinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="m18.84 12.25 1.72-1.71a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="m5.17 11.75-1.71 1.71a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      <line x1="8" y1="2" x2="8" y2="5" /><line x1="2" y1="8" x2="5" y2="8" />
      <line x1="16" y1="19" x2="16" y2="22" /><line x1="19" y1="16" x2="22" y2="16" />
    </svg>
  );
}

export default function AppRow({ app, suffix, isMemberView, canManage: canManageAll, subjectFirstName, onToggle, onDisconnect }) {
  const kind = appKind(app);
  // Device and unnamed sign-ins are never on IT's to-close list: no checkbox.
  const canManage = canManageAll && kind === 'app';
  // A repeated name ("Robusta" x6) carries a suffix everywhere it is shown or
  // announced, so the rows, the checkbox names and Disconnect can be told apart.
  const name = withSuffix(appName(app), suffix);
  const label = withSuffix(appLabel(app), suffix);
  // Never claim a date we don't have: "Access given" is from Google's audit log,
  // "Linked before" when it is older than the log reaches, nothing if unchecked.
  // The no-break spaces keep "Access given {date}" on one line on phones, and
  // the one after the dot carries "·" down with the date, so it never dangles
  // at the end of a line.
  const accessDate = app.access_given_at
    ? ` ·\u00a0Access\u00a0given\u00a0${formatDate(app.access_given_at)}`
    : app.access_given_before ? ` ·\u00a0Linked\u00a0before\u00a0${formatDate(app.access_given_before)}` : '';
  const meta = app.is_nexus
    ? 'Used to sign in to Nexus'
    : `${app.native_app ? 'Mobile or desktop app' : 'Signed in with Google'}${accessDate}`;
  const closedMeta = app.closed_at
    && (isMemberView ? `Closed by ${app.closed_by_name || 'IT'} · ${formatDate(app.closed_at)}` : `Closed by IT · ${formatDate(app.closed_at)}`);
  const disconnectedMeta = app.disconnected_at
    && (isMemberView ? `Disconnected by ${subjectFirstName || 'employee'} · ${formatDate(app.disconnected_at)}` : `Disconnected by you · ${formatDate(app.disconnected_at)}`);
  // Revoking a device sign-in would sign the person out of their phone or
  // browser; IT handles those in the Admin console.
  const showDisconnect = !isMemberView && app.still_granted && !app.is_nexus && kind !== 'device';
  const id = `ca-app-${app.id}`;
  return (
    <li className={`ca-row${canManage ? ' ca-row--check' : ''}`}>
      <AppTile app={app} />
      {canManage && (
        // The wrapper only widens the tap area to 40px on phones (a native
        // checkbox ignores padding). A tap on the ring forwards to the box; the
        // keyboard path is the checkbox itself, and its one label stays the
        // app name.
        <span className="ca-row__hit" onClick={(e) => { if (e.target === e.currentTarget) e.currentTarget.querySelector('input')?.click(); }}>
          <input id={id} type="checkbox" className="ca-row__check" checked={!!app.closed_at}
            onChange={(e) => onToggle(app, e.target.checked)} />
        </span>
      )}
      <div className="ca-row__body">
        <span className="ca-row__name">
          {/* Real label; the hidden prefix gives the checkbox its name "Account closed in {App}". */}
          {canManage
            ? <label htmlFor={id}><span className="sr-only">Account closed in </span>{name}{label !== name && <span className="sr-only">{label.slice(name.length)}</span>}</label>
            : name}
          {!app.still_granted && !app.disconnected_at && <span className="ca-badge">No longer connected in Google</span>}
        </span>
        {isUnnamed(app.display_text) && <span className="ca-row__id">{app.display_text}</span>}
        <span className="ca-row__meta">{meta}</span>
        {app.access && app.access.length > 0 && <span className="ca-row__meta ca-row__access">Can access: {app.access.join(' · ')}</span>}
        {disconnectedMeta && <span className="ca-row__meta">{disconnectedMeta}</span>}
        {closedMeta && <span className="ca-row__meta">{closedMeta}</span>}
      </div>
      {(canManage && app.closed_at) || (showDisconnect) ? (
        <div className="ca-row__action">
          {canManage && app.closed_at && (
            <button type="button" className="btn btn-secondary btn-small" onClick={() => onToggle(app, false)}>Undo</button>
          )}
          {showDisconnect && (
            <button type="button" className="btn btn-small btn-danger-outline ca-btn-icon ca-btn-disconnect" aria-label={`Disconnect ${label}`} onClick={() => onDisconnect(app)}>
              <UnlinkIcon />Disconnect
            </button>
          )}
        </div>
      ) : null}
    </li>
  );
}
